import { DashboardColumn } from '@/types/dashboard'

export const POSITION_TABLE_COLUMNS: Record<keyof DashboardColumn, string> = {
  instrument: 'Instrument',
  type: 'Type',
  size: 'Size',
  delta: 'Delta',
  deltaUSD: 'Delta ($)',
  gamma: 'Gamma',
  gammaUSD: 'Gamma ($)',
  vega: 'Vega',
  vegaUSD: 'Vega ($)',
  theta: 'Theta',
  thetaUSD: 'Theta ($)',
  rho: 'Rho',
  rhoUSD: 'Rho ($)',
  entry: 'Entry',
  entryIv: 'Entry IV',
  mark: 'Mark',
  markIv: 'Mark IV',
  pnl: 'PnL',
  pnlUSD: 'PnL ($)',
  pnlPercent: '% PnL',
  netPnl: 'Net PnL',
  realizedPnl: 'Realized PnL',
  realizedPnlUSD: 'Realized PnL ($)',
  im: 'Initial Margin',
  imUSD: 'Initial Margin ($)',
  mm: 'Maintenance Margin',
  mmUSD: 'Maintenance Margin ($)',
  liquidationPrice: 'Est. Liq. Price',
  dailyUnrealizedPnl: 'Daily PnL',
  dailyUnrealizedPnlUsd: 'Daily PnL ($)',
  realizedFunding: 'Realized Funding',
  expiry: 'Expiry',
  // Appended last on purpose: a persisted `columnsOrder` is an array of indices into
  // this key order, so inserting anywhere else would silently remap every saved order.
  // Default *display* position is set in DEFAULT_COLUMNS_ORDER below instead.
  account: 'Account',
}

const IDENTITY_COLUMN_ORDER: number[] = Object.keys(POSITION_TABLE_COLUMNS).map((_, index) => index)
const ACCOUNT_COLUMN_INDEX = IDENTITY_COLUMN_ORDER[IDENTITY_COLUMN_ORDER.length - 1]
// Default display order leads with Instrument (index 0) → Account → Type (index 1), then
// the rest in key order. This sets only the default *display order*, not what each index
// means — a saved/persisted `columnsOrder` (an index array into POSITION_TABLE_COLUMNS'
// key order) still resolves correctly, since the key order itself is untouched. Users who
// already have a saved order keep it, and Account lands at the end for them
// (position-table.tsx appends any key the saved order doesn't mention).
export const DEFAULT_COLUMNS_ORDER: number[] = [
  IDENTITY_COLUMN_ORDER[0],
  ACCOUNT_COLUMN_INDEX,
  IDENTITY_COLUMN_ORDER[1],
  ...IDENTITY_COLUMN_ORDER.slice(2, -1),
]

// Column order for visibility filtering - matches DashboardColumn keys
export const POSITION_COLUMN_ORDER = [
  'instrument',
  'account',
  'type',
  'size',
  'delta',
  'deltaUSD',
  'gamma',
  'gammaUSD',
  'vega',
  'vegaUSD',
  'theta',
  'thetaUSD',
  'rho',
  'rhoUSD',
  'entry',
  'entryIv',
  'mark',
  'markIv',
  'pnl',
  'pnlUSD',
  'pnlPercent',
  'netPnl',
  'realizedPnl',
  'realizedPnlUSD',
  'im',
  'imUSD',
  'mm',
  'mmUSD',
  'liquidationPrice',
  'dailyUnrealizedPnl',
  'dailyUnrealizedPnlUsd',
  'realizedFunding',
  'expiry',
] as const

export const POSITION_FILTER_OPTIONS = {
  instrument: [
    {
      label: 'All Instruments',
      value: 'all',
    },
    {
      label: 'Perpetual',
      value: 'perpetual',
    },
    {
      label: 'Spot',
      value: 'spot',
    },
    {
      label: 'Futures',
      value: 'expiry',
    },
    {
      label: 'Options',
      value: 'options',
    },
  ],
}

export const MOCK_ASSETS = [
  {
    label: 'BTC',
    value: 'BTC',
  },
  {
    label: 'ETH',
    value: 'ETH',
  },
  {
    label: 'SOL',
    value: 'SOL',
  },
  {
    label: 'PEPE',
    value: 'PEPE',
  },
  {
    label: 'SUI',
    value: 'SUI',
  },
  {
    label: 'APT',
    value: 'APT',
  },
  {
    label: 'ARB',
    value: 'ARB',
  },
  {
    label: 'OP',
    value: 'OP',
  },
  {
    label: 'XRP',
    value: 'XRP',
  },
  {
    label: 'LTC',
    value: 'LTC',
  },
]

export const ColumnAligns = ['l', 'l', 'r', 'r', 'r', 'r', 'r', 'r', 'r', 'r'] as const

// ColIds that should be right-aligned (entry column and all columns after it)
export const RIGHT_ALIGNED_COL_IDS = new Set([
  'avgPrice',
  'markPrice',
  'unrealizedPnl',
  'entryIv',
  'markIv',
  'pnlUSD',
  'pnlPercent',
  'netPnl',
  'realizedPnl',
  'realizedPnlUSD',
  'marginRequired',
  'imUSD',
  'maintenanceMargin',
  'mmUSD',
  'liquidationPrice',
  'dailyUnrealizedPnl',
  'dailyUnrealizedPnlUsd',
  'realizedFunding',
  'expiry',
])

/**
 * Returns the alignment for a column based on its colId.
 * Instrument and type columns are left-aligned; all others (entry and beyond) are right-aligned.
 */
export function getColAlign(colId: string): 'l' | 'r' {
  if (colId === 'symbol' || colId === 'type' || colId === 'account') return 'l'
  return 'r'
}
